import { useState } from 'react'
import { CalendarClock, X } from 'lucide-react'
import { useAddActivity } from '@/features/activity/useActivities'
import { useCreateTask } from '@/features/tasks/useTasks'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import TimeSelect from '@/components/ui/TimeSelect'
import { formatShortDate, isoInDays, todayIso } from '@/lib/dealFormat'
import type { Deal } from '@/api/types'

const fieldCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** Days ahead to prefill the next step when the deal has nothing planned. */
const DEFAULT_DAYS_AHEAD = 7

/**
 * Post-move prompt: after a deal is dropped into a non-terminal stage, capture what was said (a note on
 * the timeline) and the next step — a task, whose due date is the deal's next contact date. It doesn't
 * block the move (that already happened); Skip just dismisses.
 */
export default function NextContactDatePrompt({
  deal,
  onClose,
}: {
  deal: Deal
  pipelineId?: string
  onClose: () => void
}) {
  const createTask = useCreateTask(deal.id)
  const addNote = useAddActivity(deal.id)
  // A next step already planned ahead stays as it is; otherwise suggest one a week out.
  const planned = deal.nextContactDate && deal.nextContactDate >= todayIso() ? deal.nextContactDate : null
  const [note, setNote] = useState('')
  const [title, setTitle] = useState('Follow up')
  const [date, setDate] = useState(planned ? '' : isoInDays(DEFAULT_DAYS_AHEAD))
  const [time, setTime] = useState('')
  const [error, setError] = useState<string | null>(null)

  function save() {
    if (date && date < todayIso()) {
      setError("The next step can't be in the past")
      return
    }
    if (note.trim()) {
      addNote.mutate({ activityType: 'note', body: note.trim() })
    }
    if (!date) {
      onClose()
      return
    }
    // A time is the user's local time; a date alone is due by the end of that day (as the Tasks card does).
    const due = new Date(`${date}T${time || '23:59:59'}`).toISOString()
    createTask.mutate(
      { title: title.trim() || 'Follow up', dueDate: due, dueDateLocal: date },
      { onSuccess: onClose, onError: (err) => setError(apiErrorMessage(err)) },
    )
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <CalendarClock size={16} className="text-brand-primary" />
            <h2 className="text-sm font-semibold text-brand-darkest">Log the move</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            “{deal.title}” moved stage. Add a note about the move and plan the next step.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Note</label>
            <input
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Spoke with Fred — waiting on the house wash"
              maxLength={500}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Next step</label>
            {planned && (
              <p className="text-[11px] text-gray-500 mb-1.5">
                Already planned for {formatShortDate(planned)}. Add another only if there’s a new step.
              </p>
            )}
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What to do" className={fieldCls} />
            <div className="mt-2 flex gap-2">
              <input
                type="date"
                aria-label="Next step date"
                min={todayIso()}
                value={date}
                onChange={(e) => {
                  setDate(e.target.value)
                  setError(null)
                }}
                className={`${fieldCls} flex-1 min-w-0`}
              />
              <TimeSelect aria-label="Next step time" value={time} onChange={setTime} emptyLabel="Any time" disabled={!date} size="md" className="w-32 shrink-0" />
            </div>
            {error ? (
              <p className="text-danger text-xs mt-1">{error}</p>
            ) : (
              <p className="text-[11px] text-gray-400 mt-1">
                Saved as a task — its date is the deal’s next contact. Leave the date empty for no new step.
              </p>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-brand-dark">
            Skip
          </button>
          <button
            onClick={save}
            disabled={createTask.isPending}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            {createTask.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
